import { z } from 'zod';
import { COUNTRIES } from './collections.js';

// ---------------------------------------------------------------------------
// Organisation data: General Information + Responsible Individuals
// (Org Settings blocks). See docs/CONTRACTS.md.
// ---------------------------------------------------------------------------

export const OrgProfileSchema = z.object({
  orgId: z.string(),
  businessName: z.string(),
  companyDescription: z.string(),
  addressLine: z.string(),
  postcode: z.string(),
  city: z.string(),
  country: z.string(),
  registrationNumber: z.string(),
  hasEmployees: z.boolean(),
  hqInEu: z.boolean(),
  companyEmail: z.string(),
  updatedAt: z.string().nullable(),
});
export type OrgProfile = z.infer<typeof OrgProfileSchema>;

export const UpdateOrgProfileInputSchema = z.object({
  businessName: z.string().max(200).default(''),
  companyDescription: z.string().max(2000).default(''),
  addressLine: z.string().max(300).default(''),
  postcode: z.string().max(20).default(''),
  city: z.string().max(100).default(''),
  // Same list as record collections. Legacy free-text rows still read back
  // via GET; the admin is steered to reselect on the next save.
  country: z
    .string()
    .max(100)
    .default('')
    .refine((v) => v === '' || COUNTRIES.includes(v), {
      message: 'Choose a country from the list',
    }),
  registrationNumber: z.string().max(50).default(''),
  hasEmployees: z.boolean().default(false),
  hqInEu: z.boolean().default(false),
  companyEmail: z.string().email().or(z.literal('')).default(''),
});
export type UpdateOrgProfileInput = z.infer<typeof UpdateOrgProfileInputSchema>;

/** The fixed set of responsible-individual roles an org assigns to members. */
export const ORG_RESPONSIBILITY_ROLES: ReadonlyArray<{
  key: OrgResponsibilityRoleKey;
  label: string;
}> = [
  { key: 'organisation_owner', label: 'Organisation Owner' },
  { key: 'cto', label: 'Chief Technology Officer' },
  { key: 'cio', label: 'Chief Information Officer' },
  { key: 'marketing_owner', label: 'Marketing & Communications Owner' },
  { key: 'infosec_owner', label: 'Information Security Owner' },
  { key: 'legal_owner', label: 'Legal & Regulatory Owner' },
  { key: 'dpo', label: 'Data Protection Officer' },
  { key: 'it_owner', label: 'IT Owner' },
  { key: 'health_safety_owner', label: 'Health and Safety Owner' },
  { key: 'hr_owner', label: 'HR Owner' },
  { key: 'isms_owner', label: 'ISMS Owner' },
  { key: 'isms_internal_auditor', label: 'ISMS Internal Auditor' },
  { key: 'qms_owner', label: 'QMS Owner' },
  { key: 'qms_internal_auditor', label: 'QMS Internal Auditor' },
  { key: 'quality_owner', label: 'Quality Owner' },
];

export const OrgResponsibilityRoleKeySchema = z.enum([
  'organisation_owner',
  'cto',
  'cio',
  'marketing_owner',
  'infosec_owner',
  'legal_owner',
  'dpo',
  'it_owner',
  'health_safety_owner',
  'hr_owner',
  'isms_owner',
  'isms_internal_auditor',
  'qms_owner',
  'qms_internal_auditor',
  'quality_owner',
]);
export type OrgResponsibilityRoleKey = z.infer<typeof OrgResponsibilityRoleKeySchema>;

export const OrgResponsibilitySchema = z.object({
  roleKey: OrgResponsibilityRoleKeySchema,
  label: z.string(),
  userId: z.string().nullable(),
  userName: z.string().nullable(),
  userEmail: z.string().nullable(),
});
export type OrgResponsibility = z.infer<typeof OrgResponsibilitySchema>;

export const UpdateOrgResponsibilitiesInputSchema = z.object({
  assignments: z
    .array(
      z.object({
        roleKey: OrgResponsibilityRoleKeySchema,
        /** null clears the assignment. Must be an org member otherwise. */
        userId: z.string().nullable(),
      }),
    )
    .min(1),
});
export type UpdateOrgResponsibilitiesInput = z.infer<typeof UpdateOrgResponsibilitiesInputSchema>;
